<script setup lang="ts">
import { PhGauge, PhMapPinLine } from '@phosphor-icons/vue'
import { computed } from 'vue'

import RouteCodeBadge from '@/components/transit/RouteCodeBadge.vue'
import type { MapVehicle } from '@/types'
import { directionLabel } from '@/utils/direction'
import { formatDistance, formatEta } from '@/utils/format'

const props = withDefaults(
  defineProps<{
    vehicle: MapVehicle
    routeName?: string
    /** Group headers already show route and direction metadata. */
    showRouteMeta?: boolean
  }>(),
  { showRouteMeta: true },
)

const eta = computed(() => formatEta(props.vehicle.minutesToNextStop))
const distance = computed(() =>
  props.vehicle.distanceToStop === undefined ? '' : formatDistance(props.vehicle.distanceToStop),
)
const inlineEta = computed(() => (eta.value === '—' ? eta.value : `${eta.value} mins`))
</script>

<template>
  <button
    type="button"
    class="flex w-full border-b text-left transition-colors hover:bg-muted"
    :class="[
      props.showRouteMeta
        ? 'items-start gap-3 px-3 py-3'
        : 'items-center gap-2 px-3 py-2 whitespace-nowrap',
      vehicle.source === 'last-known' ? 'opacity-60' : '',
    ]"
  >
    <RouteCodeBadge v-if="props.showRouteMeta" :code="vehicle.routeCode" />

    <span class="min-w-0 flex-1">
      <span v-if="props.showRouteMeta" class="block text-sm">
        {{ routeName ?? vehicle.routeCode }}
        <!-- Direction is colour-coded in the mockup, but colour is never the only
             encoding — the word itself carries the meaning. -->
        <span
          class="font-bold"
          :class="vehicle.direction === 'outbound' ? 'text-route-outbound' : 'text-route-inbound'"
        >
          ({{ directionLabel(vehicle.direction) }})
        </span>
      </span>

      <span
        class="flex items-center gap-x-3 gap-y-1 text-xs text-muted-foreground"
        :class="props.showRouteMeta ? 'mt-1 flex-wrap' : 'overflow-hidden whitespace-nowrap'"
      >
        <span v-if="vehicle.plate" class="font-bold text-primary">{{ vehicle.plate }}</span>
        <span v-if="vehicle.speed !== undefined" class="flex items-center gap-1">
          <PhGauge class="size-3.5" />
          {{ vehicle.speed }} km/h
        </span>
        <span v-if="distance" class="flex items-center gap-1">
          <PhMapPinLine class="size-3.5" />
          {{ distance }}
        </span>
      </span>
    </span>

    <span
      v-if="!props.showRouteMeta"
      class="shrink-0 text-xs font-bold whitespace-nowrap"
      :class="vehicle.minutesToNextStop === undefined ? 'text-muted-foreground' : 'text-success'"
    >
      {{ inlineEta }}
    </span>

    <span
      v-else
      class="flex shrink-0 flex-col items-center border px-2 py-1"
      :class="vehicle.minutesToNextStop === undefined ? 'border-border' : 'border-success'"
    >
      <span
        class="text-base font-bold"
        :class="vehicle.minutesToNextStop === undefined ? 'text-muted-foreground' : 'text-success'"
      >
        {{ eta }}
      </span>
      <span class="text-xs text-muted-foreground">mins</span>
    </span>
  </button>
</template>
